import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {
  agentsByGroup,
  TIERS,
  TIER_PRICE,
  includedInTier,
} from '../../data/agentMeta';

const { FiCheck, FiPlus, FiMinus } = FiIcons;

const tint = (hex, alpha = 0.12) => {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const Cell = ({ agent, tier }) => {
  const state = includedInTier(agent, tier);
  if (state === true) {
    return <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mx-auto" />;
  }
  if (state === 'addon') {
    return (
      <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-secondary-700 whitespace-nowrap">
        <SafeIcon icon={FiPlus} className="w-3 h-3" /> ${agent.addonPriceUsd}
      </span>
    );
  }
  return <SafeIcon icon={FiMinus} className="w-4 h-4 text-gray-200 mx-auto" />;
};

/* Data-driven, non-editable agent x tier matrix rendered from the registry + pricing.
 * Green check = included in that plan; "+$N" = available as a paid add-on; dash = not in plan. */
const AgentTierMatrix = () => {
  const groups = agentsByGroup();
  const hasAddons = groups.some((g) => g.items.some((a) => a.addonPriceUsd != null));

  return (
    <div>
      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <table className="w-full border-collapse min-w-[640px]">
          <thead className="sticky top-0">
            <tr className="border-b-2 border-gray-200 bg-white">
              <th className="text-left py-4 px-3 text-gray-700 text-sm">Agent / capability</th>
              {TIERS.map((t) => (
                <th key={t} className="py-4 px-3 text-center">
                  <div className={`text-sm font-bold ${t === 'Author' ? 'text-primary-600' : 'text-gray-700'}`}>{t}</div>
                  <div className="text-xs text-gray-400 font-normal">{TIER_PRICE[t]}/mo</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <React.Fragment key={g.group}>
                <tr>
                  <td colSpan={TIERS.length + 1} className="pt-5 pb-1.5 px-3">
                    <span className="text-xs font-bold uppercase tracking-wide text-gray-400">{g.meta.short}</span>
                  </td>
                </tr>
                {g.items.map((a) => (
                  <tr key={a.id} className="border-b border-gray-100 hover:bg-parchment-50/60">
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2.5">
                        <span className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: tint(a.identityColor), color: a.identityColor }}>
                          <SafeIcon icon={a.Icon} className="w-3.5 h-3.5" />
                        </span>
                        <span>
                          <span className="text-sm font-medium text-gray-800 block leading-tight">{a.name}</span>
                          <span className="text-xs text-gray-400 leading-tight line-clamp-1">{a.tagline}</span>
                        </span>
                      </div>
                    </td>
                    {TIERS.map((t) => (
                      <td key={t} className={`py-3 px-3 text-center ${t === 'Author' ? 'bg-primary-50/40' : ''}`}>
                        <Cell agent={a} tier={t} />
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1.5"><SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500" /> Included in plan</span>
        {hasAddons && (
          <span className="inline-flex items-center gap-1.5"><SafeIcon icon={FiPlus} className="w-3.5 h-3.5 text-secondary-700" /> Available as a paid add-on (per-use or monthly)</span>
        )}
        <span className="inline-flex items-center gap-1.5"><SafeIcon icon={FiMinus} className="w-4 h-4 text-gray-300" /> Not in this plan</span>
      </div>
    </div>
  );
};

export default AgentTierMatrix;
